import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { esAdministrativo } from '@kodica/shared';

import logo from '../assets/marca/kodica-primary.svg';
import type { CondominioDisponible } from '@kodica/shared';

import { useRecurso } from '../lib/consultas';
import { useSesion } from '../lib/sesion';
import { Iconos, type NombreIcono } from './iconos';

/**
 * Estructura del diseño "Kodica Interface Suite": barra superior fija de 64px
 * con la marca, y barra lateral fija de 256px con iconos. El contenido se
 * desplaza con `pt-16 lg:pl-64` en vez de usar flex, para que las dos barras
 * queden realmente fijas al hacer scroll en una tabla larga.
 */

interface Seccion {
  a: string;
  texto: string;
  icono: NombreIcono;
  soloAdmin?: boolean;
  /**
   * Existe solo donde hay reja.
   *
   * No es una cuestión de permisos: en la nube esas rutas **no están
   * registradas** en el API, así que la pantalla no falla con un 403 sino con
   * un «Cannot GET». Un menú que ofrece lo que no existe es peor que uno corto:
   * la persona pincha, no pasa nada comprensible, y ya no sabe si el sistema
   * está roto o es que no le toca.
   */
  soloCaseta?: boolean;
  /**
   * Existe solo en la nube.
   *
   * El reverso de [soloCaseta], y por el mismo motivo: son pantallas para
   * **editar** cosas que la nube manda hacia abajo —la topología, el personal,
   * el fraccionamiento—. En la caseta esas rutas del API ya no están
   * registradas, así que ofrecerlas en el menú es mandar a alguien a un «Cannot
   * POST». Y aunque estuvieran, lo que guardara ahí se revertiría en la
   * siguiente sincronización.
   *
   * El padrón **no** lleva esta marca: el guardia lo consulta todo el turno. Ahí
   * lo que se esconde son los botones de editar, no la pantalla.
   */
  soloNube?: boolean;
}

const SECCIONES: Seccion[] = [
  { a: '/', texto: 'Panel', icono: 'panel', soloAdmin: true },
  // Va arriba y sin `soloAdmin`: es la pantalla que el guardia usa todo el
  // turno, y es quien menos tiempo tiene para buscarla en un menú.
  { a: '/caseta', texto: 'Caseta', icono: 'panel', soloCaseta: true },
  // La ve el guardia también: es su ventana al carril de condóminos, que abre
  // sin que nadie intervenga.
  { a: '/lecturas', texto: 'Lecturas de tag', icono: 'credencial', soloCaseta: true },
  { a: '/padron', texto: 'Padrón', icono: 'personas' },
  // Sin `soloAdmin`: el guardia necesita poder revisar lo que pasó en su reja
  // cuando alguien reclama a media noche. Las pestañas que no le tocan —los
  // movimientos del sistema y los avisos— no le aparecen, y el API se las niega.
  // Solo de caseta porque la ruta que valida el código vive en `AccesosModule`,
  // que no se carga en la nube: aprobar y liberar una visita se decide en la
  // reja, con el guardia ahí.
  { a: '/validar', texto: 'Leer código', icono: 'buscar', soloCaseta: true },
  { a: '/adentro', texto: 'Adentro ahora', icono: 'personas', soloCaseta: true },
  { a: '/historial', texto: 'Historial', icono: 'buscar' },
  {
    a: '/topologia',
    texto: 'Accesos y aparatos',
    icono: 'aparatos',
    soloAdmin: true,
    soloNube: true,
  },
  { a: '/avisos', texto: 'Avisos', icono: 'avisos', soloAdmin: true },
  // «Tipos de visita» no está en el menú a petición del cliente: es una
  // clasificación que la administración no usa y que solo hacía más largo el
  // menú. La pantalla sigue existiendo en `/tipos-visita` —los tipos siguen
  // haciendo falta, el kiosco pide uno por omisión al registrar— así que se
  // esconde, no se borra.
  { a: '/telefonia', texto: 'Telefonía', icono: 'telefono', soloAdmin: true, soloCaseta: true },
  { a: '/usuarios', texto: 'Personal', icono: 'cuenta', soloAdmin: true, soloNube: true },
  {
    a: '/condominio',
    texto: 'Fraccionamiento',
    icono: 'unidades',
    soloAdmin: true,
    soloNube: true,
  },
];

/**
 * En qué fraccionamiento se está trabajando, y cómo cambiarlo.
 *
 * Solo lo ve el SUPER_ADMIN de Kodica. Para los demás la respuesta es siempre
 * la misma —el suyo— y un selector de un elemento no informa, estorba.
 *
 * Y para él no es decoración: **todo lo que hay debajo cambia** según cuál esté
 * activo. Sin esto en pantalla, un administrador de plataforma edita el padrón
 * de un fraccionamiento creyendo que es el de otro, y no hay nada que se lo
 * diga hasta que alguien reclama.
 */
function SelectorDeFraccionamiento() {
  const { usuario } = useSesion();
  const esSuper = usuario?.rol === 'SUPER_ADMIN';
  // `null` no consulta: solo se pregunta cuando la respuesta puede ser distinta.
  const { datos } = useRecurso<CondominioDisponible[]>(esSuper ? '/auth/condominios' : null);

  if (!esSuper) return null;

  // **Cuál está activo sale de la sesión, no de la respuesta.** El `activo` que
  // manda el API lo calcula con la sesión del momento en que se preguntó, así
  // que en cuanto alguien cambia de fraccionamiento esa foto ya es vieja: el
  // selector se quedaba diciendo «Sin fraccionamiento» después de elegir uno.
  //
  // De la lista solo hace falta el nombre, y los nombres no cambian al cambiar
  // de fraccionamiento. Cruzándolos con `usuario.condominios`, que sí se
  // actualiza porque viene del token nuevo, el rótulo es correcto al instante y
  // sin volver a preguntar.
  const activo = usuario.condominios[0] ?? null;
  const actual = activo ? datos?.find((c) => c.id === activo) : undefined;

  return (
    <Link
      to="/fraccionamiento"
      className="mx-4 mb-2 flex items-center gap-3 rounded-lg border border-outline-variant px-3 py-2 transition-colors hover:bg-surface-container-high"
    >
      <Iconos.unidades tam={20} className="shrink-0 text-on-surface-variant" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-label-bold text-on-surface">
          {actual?.nombre ?? 'Sin fraccionamiento'}
        </p>
        <p className="text-label-sm text-primary">Cambiar</p>
      </div>
    </Link>
  );
}

function EnlaceSeccion({ s, alNavegar }: { s: Seccion; alNavegar?: () => void }) {
  const Icono = Iconos[s.icono];
  return (
    <NavLink
      to={s.a}
      end={s.a === '/'}
      onClick={alNavegar}
      className={({ isActive }) =>
        `mx-2 flex items-center gap-4 rounded-lg px-4 py-3 text-label-bold transition-all ${
          isActive
            ? 'bg-secondary-container text-on-secondary-container'
            : 'text-on-surface-variant hover:bg-surface-container-highest'
        }`
      }
    >
      <Icono tam={24} />
      <span>{s.texto}</span>
    </NavLink>
  );
}

/**
 * La ficha de quien entró, que es además el camino a su contraseña.
 *
 * Va al pie del menú y no en una sección propia: se toca una vez al año, y
 * donde la gente busca sus cosas es donde está su nombre.
 *
 * **Es un componente y no markup repetido porque lo usan los dos menús.**
 * Estuvo escrito solo dentro de la barra lateral, que es `hidden lg:flex`, y el
 * resultado fue que desde un teléfono no se podía cambiar la contraseña: el
 * enlace existía y no había manera de verlo. Compartirlo es lo que impide que
 * vuelvan a separarse.
 */
function FichaDeUsuario({
  usuario,
  alNavegar,
}: {
  usuario: {
    nombre: string;
    apellidoPaterno?: string | null;
    apellidoMaterno?: string | null;
  } | null;
  alNavegar?: () => void;
}) {
  return (
    <div className="mt-auto border-t border-outline-variant p-4">
      <Link
        to="/contrasena"
        onClick={alNavegar}
        className="flex items-center gap-3 rounded-lg px-4 py-2 transition-colors hover:bg-surface-container-high"
      >
        <Iconos.cuenta tam={32} className="text-outline" />
        <div className="min-w-0">
          <p className="truncate text-label-bold text-on-surface">
            {usuario?.nombre} {usuario?.apellidoPaterno ?? ''} {usuario?.apellidoMaterno ?? ''}
          </p>
          <p className="truncate text-label-sm text-primary">Cambiar contraseña</p>
        </div>
      </Link>
    </div>
  );
}

export function Layout() {
  const { usuario, cerrar, rolServidor } = useSesion();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const admin = usuario ? esAdministrativo(usuario.rol) : false;
  const secciones = SECCIONES.filter(
    (s) =>
      (admin || !s.soloAdmin) &&
      (rolServidor === 'caseta' || !s.soloCaseta) &&
      (rolServidor === 'nube' || !s.soloNube),
  );

  return (
    <>
      <nav className="fixed left-0 top-0 z-50 flex h-16 w-full items-center justify-between border-b border-outline-variant bg-surface px-margin-mobile lg:px-margin-desktop">
        <div className="flex items-center gap-stack-md">
          <button
            onClick={() => setMenuAbierto((v) => !v)}
            aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuAbierto}
            className="rounded-full p-2 text-primary transition-colors hover:bg-surface-container-high lg:hidden"
          >
            {menuAbierto ? <Iconos.cerrar /> : <Iconos.menu />}
          </button>
          {/* El logotipo y no la palabra escrita. Los SVG de marca ya estaban
              en el proyecto y solo los usaba la pantalla de entrada: dentro de
              la app la marca era texto, y se notaba justo al pasar de una a la
              otra. `alt` con el nombre para que un lector de pantalla siga
              diciendo lo mismo que antes. */}
          <img src={logo} alt="Kodica" className="h-7 w-auto" />
        </div>

        <div className="flex items-center gap-stack-md">
          <span className="hidden text-label-bold text-on-surface-variant sm:inline">
            {usuario?.nombre} {usuario?.apellidoPaterno ?? ''} {usuario?.apellidoMaterno ?? ''}
          </span>
          <button
            onClick={cerrar}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            className="flex h-10 w-10 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high"
          >
            <Iconos.salir />
          </button>
        </div>
      </nav>

      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-outline-variant bg-surface-container-low pt-16 lg:flex">
        {/* **Sin el logotipo otra vez.** La barra de arriba ya lo lleva y la
            lateral empieza justo debajo: se veian los dos a la vez, uno encima
            del otro, gastando el sitio donde deberian empezar las secciones.
        
            La frase se queda: dice que es esto, que el logotipo solo no dice. */}
        <div className="px-6 py-8">
          <p className="text-body-md text-on-surface-variant">Control de acceso</p>
        </div>

        <SelectorDeFraccionamiento />

        <div className="flex flex-1 flex-col gap-2 overflow-y-auto py-4">
          {secciones.map((s) => (
            <EnlaceSeccion key={s.a} s={s} />
          ))}
        </div>

        <FichaDeUsuario usuario={usuario} />
      </aside>

      {/* En móvil la lateral se despliega sobre el contenido. El panel se usa
          en escritorio, pero el jefe de seguridad a veces entra desde tablet. */}
      {menuAbierto && (
        <div className="fixed inset-0 top-16 z-40 lg:hidden">
          <button
            aria-label="Cerrar menú"
            onClick={() => setMenuAbierto(false)}
            className="absolute inset-0 bg-black/40"
          />
          {/* **Lo mismo que la lateral, y no por simetría.** Durante un tiempo
              aquí solo estaban las secciones, y como la lateral es `hidden
              lg:flex`, desde un teléfono no había forma de llegar a cambiar la
              contraseña ni de ver en qué fraccionamiento estabas trabajando:
              las dos cosas existían y ninguna se podía alcanzar. */}
          <div className="relative flex h-full w-64 flex-col border-r border-outline-variant bg-surface-container-low">
            <SelectorDeFraccionamiento />

            <div className="flex flex-1 flex-col gap-2 overflow-y-auto py-4">
              {secciones.map((s) => (
                <EnlaceSeccion key={s.a} s={s} alNavegar={() => setMenuAbierto(false)} />
              ))}
            </div>

            <FichaDeUsuario usuario={usuario} alNavegar={() => setMenuAbierto(false)} />
          </div>
        </div>
      )}

      <main className="min-h-screen pt-16 lg:pl-64">
        <div className="p-gutter lg:p-margin-desktop">
          <Outlet />
        </div>
      </main>
    </>
  );
}
